import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../useI18n.js';
import { Avatar } from './Avatar';
import { Confetti } from './Confetti';
import { MessageBody } from './MessageBody';

// Fil de conversation (fenetre de contexte) : plusieurs bulles auteur + texte.
function ConvThread({ messages = [], compact }) {
  return (
    <div className={`disc-thread${compact ? ' compact' : ''}`}>
      {messages.map((m, i) => (
        <div key={i} className="disc-msg">
          <Avatar url={m.avatarUrl} label={m.name} size={compact ? 22 : 28} className="disc-av" />
          <div className="disc-msg-body">
            <div className="disc-author">{m.name}</div>
            <div className="disc-text"><MessageBody content={m.text} imgClassName="disc-img" /></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Bandeau "auteur cible" : on ecrit dans le ton de cette personne.
function TargetBadge({ target, hint }) {
  const { t } = useI18n();
  return (
    <div className="disc-target">
      <Avatar url={target?.avatarUrl} label={target?.name} size={34} className="disc-av" />
      <div className="disc-target-txt">
        <div className="disc-target-lbl">{hint || (t('discussionReplyAs') || 'Réponds comme')}</div>
        <div className="disc-target-name">{target?.name || '???'}</div>
      </div>
    </div>
  );
}

// ---- Phase 1 : ecriture d'un bluff (dans le ton de l'auteur cible) ----
function WritePhase({ context, timeLeft, onSubmit, submitted, progress }) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const armedRef = useRef(false);
  useEffect(() => {
    if (timeLeft > 1) armedRef.current = true;
    if (armedRef.current && !submitted && timeLeft <= 1 && text.trim()) onSubmit(text.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  return (
    <div className="disc-round">
      <div className="create-head">
        <div className="create-prompt"><span className="cp-label">{t('discussionContext') || 'La discussion'}</span></div>
        {timeLeft > 0 && <span className="chrono-timer">{timeLeft}s</span>}
      </div>
      <ConvThread messages={context?.messages} />
      <TargetBadge target={context?.target} />
      {submitted ? (
        <div className="mashup-status">{t('discussionWaitLies') || 'En attente des autres...'} {progress.length ? `(${progress.length})` : ''}</div>
      ) : (
        <div className="disc-write">
          <textarea
            className="disc-input" maxLength={180} rows={2}
            placeholder={t('discussionWritePlaceholder') || 'Invente sa réponse...'}
            value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (text.trim()) onSubmit(text.trim()); } }}
          />
          <button type="button" className="btn primary create-submit" disabled={!text.trim()} onClick={() => text.trim() && onSubmit(text.trim())}>
            {t('discussionSubmitLie') || 'Valider mon bluff'}
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Phase 2 : vote (retrouver la vraie reponse de l'auteur cible) ----
function VotePhase({ context, options, timeLeft, onVote, myLie }) {
  const { t } = useI18n();
  const [voted, setVoted] = useState(null);
  // On exclut MON option par PROPRIETAIRE (drapeau serveur "mine"), pas par texte :
  // si un autre joueur a ecrit exactement le meme bluff, son option reste affichee.
  const shown = options.filter((o) => !o.mine);
  const vote = (id) => { if (voted) return; setVoted(id); onVote(id); };
  const name = context?.target?.name || '???';

  return (
    <div className="disc-round">
      <div className="create-head">
        <div className="create-prompt"><span className="cp-label">{(t('discussionWhichReal') || 'Quelle est la vraie réponse de')} {name} ?</span></div>
        {timeLeft > 0 && <span className="chrono-timer">{timeLeft}s</span>}
      </div>
      <ConvThread messages={context?.messages} compact />
      <div className="disc-options">
        {shown.map((o) => (
          <button key={o.id} type="button" className={`disc-opt${voted === o.id ? ' picked' : ''}${voted && voted !== o.id ? ' dim' : ''}`} disabled={!!voted} onClick={() => vote(o.id)}>
            {o.text}
          </button>
        ))}
        {myLie && <div className="disc-mylie">🃏 {t('discussionYourLie') || 'ton bluff'} : « {myLie} »</div>}
      </div>
      {voted && <div className="mashup-status">{t('discussionVoted') || 'Voté !'}</div>}
    </div>
  );
}

// ---- Phase 3 : reveal ----
function RevealPhase({ reveal, myPseudo }) {
  const { t } = useI18n();
  const options = reveal?.options || [];
  const ordered = [...options].sort((a, b) => (a.isReal === b.isReal ? 0 : a.isReal ? 1 : -1)); // vraie en dernier
  const [shown, setShown] = useState(0);
  useEffect(() => {
    setShown(0);
    const timers = [];
    ordered.forEach((_, i) => timers.push(setTimeout(() => setShown((s) => Math.max(s, i + 1)), 700 + i * 700)));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal]);

  const myGain = reveal?.gained?.[myPseudo] || 0;
  const name = reveal?.targetAuthor?.name || reveal?.realAuthor || '???';

  return (
    <div className="disc-round">
      <div className="create-head"><div className="create-prompt"><span className="cp-label">{t('discussionResults') || 'Résultats'}</span></div></div>
      <ConvThread messages={reveal?.context} compact />
      <div className="disc-reveal-list">
        {ordered.map((o, i) => {
          const visible = i < shown;
          return (
            <div key={o.id} className={`disc-rev-item${visible ? ' show' : ''}${o.isReal ? ' real' : ''}`}>
              <div className="disc-rev-text">{o.text}</div>
              <div className="disc-rev-meta">
                {o.isReal
                  ? <span className="disc-badge real">✅ {name}</span>
                  : <span className="disc-badge">🃏 {o.owner}{(o.voters?.length ? ` · ${o.voters.length} ${t('discussionFooled') || 'piégé'}` : '')}</span>}
                <div className="disc-voters">
                  {(o.voters || []).map((v) => <Avatar key={v} url={null} label={v} size={22} className="disc-voter" />)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {myGain > 0 && shown >= ordered.length && <div className="disc-gain">+{myGain} 🎉</div>}
      <Confetti active={shown >= ordered.length && myGain >= 2} count={180} />
    </div>
  );
}

export function DiscussionRound({ phase, context, options = [], reveal, timeLeft, submitLie, submitVote, progress = [], myPseudo }) {
  const [myLie, setMyLie] = useState('');
  useEffect(() => { setMyLie(''); }, [context]); // reset a chaque manche (nouvel objet contexte)

  if (phase === 'voting') {
    return <VotePhase context={context} options={options} timeLeft={timeLeft} onVote={submitVote} myLie={myLie} />;
  }
  if (phase === 'reveal') {
    return <RevealPhase reveal={reveal} myPseudo={myPseudo} />;
  }
  if (phase !== 'writing') {
    return <div className="disc-round"><div className="mashup-status">…</div></div>;
  }
  const onSubmit = (text) => { setMyLie(text); submitLie(text); };
  return <WritePhase key={context?.target?.name} context={context} timeLeft={timeLeft} onSubmit={onSubmit} submitted={!!myLie} progress={progress} />;
}
